/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib types
import { ReactNode } from 'react';
import { ModalProps } from '@mantine/core';

// Local types
import DialogWorkspaceEdit from '../Workspace/DialogWorkspaceEdit';
import DialogUserGroupPicker from '../User/DialogUserGroupPicker';
import DialogUserBulkInvite from '../User/DialogUserBulkInvite';
import DialogResourceEdit from '../Resource/DialogResourceEdit';
import DialogResourceCreateFolder from '../Resource/DialogResourceCreateFolder';
import DialogResourceMove from '../Resource/DialogResourceMove';
import DialogResourceUpload from '../Resource/DialogResourceUpload';
import DialogGroupWizard from '../Group/DialogGroupWizard';
import DialogRedaction from '../Redaction/DialogRedaction';
import DialogWorkspaceArchive from '../Workspace/DialogWorkspaceArchive';
import DialogRedactKeywords from '../Redaction/DialogRedactKeywords';
import DialogQuestion from '@app/shared/ui-kit/organisms/DialogQuestion';
import DialogSupportConfirmation from '@app/features/Support/QuestionMenu/Thread/DialogSupportConfirmation';
import DialogQuestionPriority from '@app/features/Support/PageSupport/SupportActions/DialogQuestionPriority';
import DialogBulkConfirmAnswer from '@app/features/Support/PageSupport/SupportActions/DialogBulkConfirmAnswer';
import DialogQuestionOwner from '@app/features/Support/DialogQuestionOwner';
import DialogSupportResource from '@app/features/Support/DialogSupportResource';
import DialogQuestionResolutionStatus from '@app/features/Support/PageSupport/SupportActions/DialogQuestionResolutionStatus';
import DialogQuestionGroup from '@features/Support/PageSupport/SupportActions/DialogQuestionGroup';
import DialogImportQuestion from '@features/Support/PageSupport/DialogImportQuestion';
import DialogCreateFollowUp from '@features/Support/QuestionMenu/Thread/DialogCreateFollowUp';
import { Resource } from '@app/types/blackroom-api';
import { CreatedByLight } from '@app/shared/helpers/file';
import DialogQuestionTopics from '../Support/PageSupport/SupportActions/DialogQuestionTopics';
import DialogResourceCopy from '../Resource/DialogResourceCopy';
import DialogImportRequestList from '../RequestList/DialogImportRequestList';
import DialogReclassifyDocument from '../RequestList/DialogReclassifyDocument';
import DialogCreateRequestListItem from '../RequestList/DialogCreateRequestListItem';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

/* ****************************** SHARED TYPES ****************************** */

export const dialogsList = {
  'dialog-workspace-edit': DialogWorkspaceEdit,
  'dialog-user-bulk-invite': DialogUserBulkInvite,
  'dialog-user-group-picker': DialogUserGroupPicker,
  'dialog-resource-edit': DialogResourceEdit,
  'dialog-resource-create-folder': DialogResourceCreateFolder,
  'dialog-resource-move': DialogResourceMove,
  'dialog-resource-upload': DialogResourceUpload,
  'dialog-group-wizard': DialogGroupWizard,
  'dialog-redaction': DialogRedaction,
  'dialog-workspace-archive': DialogWorkspaceArchive,
  'dialog-redact-keywords': DialogRedactKeywords,
  'dialog-support-question': DialogQuestion,
  'dialog-support-confirmation': DialogSupportConfirmation,
  'dialog-support-question-priority': DialogQuestionPriority,
  'dialog-support-bulk-confirm-answer': DialogBulkConfirmAnswer,
  'dialog-support-question-owner': DialogQuestionOwner,
  'dialog-support-resource': DialogSupportResource,
  'dialog-support-question-resolution-status': DialogQuestionResolutionStatus,
  'dialog-support-question-group': DialogQuestionGroup,
  'dialog-import-question': DialogImportQuestion,
  'dialog-support-create-follow-up': DialogCreateFollowUp,
  'dialog-support-question-topics': DialogQuestionTopics,
  'dialog-resource-copy': DialogResourceCopy,
  'dialog-request-list-import': DialogImportRequestList,

  'dialog-reclassify-document': DialogReclassifyDocument,
  'dialog-create-request-list-item': DialogCreateRequestListItem,
} as const;

export type DialogsList = typeof dialogsList;
export type DialogIdentifier = keyof DialogsList;

export type DialogPropsWildcard<Identifier extends DialogIdentifier> =
  DialogProps & Parameters<DialogsList[Identifier]>[0];

/* ***************************** COMPONENT PROPS **************************** */

export type DialogResourceProps = Pick<
  Resource,
  | 'id'
  | 'type'
  | 'name'
  | 'createdAt'
  | 'isFavorite'
  | 'pspdfKitId'
  | 'workspaceId'
  | 'size'
  | 'mimeType'
> & { createdBy: CreatedByLight };

export interface DialogProps {
  dialogResources?: DialogResourceProps[];
  dialogResourceIds?: string[];
  modalProps?: Partial<ModalProps>;
}

export interface DialogProviderProps {
  children: ReactNode;
}
